<script setup>

import SideSlider from "~/components/templates/SideSlider.vue";
import SpoilerTabs from "~/components/molecules/SpoilerTabs.vue";
import FilterGrid from "~/components/molecules/FIlterGrid.vue";
import {useRoute, useRouter} from "vue-router";
import {watch} from "vue";
import {useCustomFetch} from '~/composables/customFetch.js';

const props = defineProps({
  openFilter: Number,
})

const route = useRoute();
const router = useRouter();

const categorySlugFromPath = (path) =>
  String(path || '')
    .split('/')
    .filter(Boolean)
    .filter((part) => !part.includes('='))
    .join('/')

const normalizePath = (path) => {
  const cleaned = String(path || '').replace(/\/+$/, '')
  return cleaned.startsWith('/') ? cleaned : `/${cleaned}`
}

const slug = ref(categorySlugFromPath(
  Array.isArray(route.params.slug) ? route.params.slug.join('/') : route.params.slug
))
const key = ref(Math.random());

const active = ref(false);
const filter = ref([]);
const emit = defineEmits(['change']);

const rangeAbsoluteValues = ref({})

const filterTitles = computed(() =>
  Object.fromEntries((filter.value || []).map(item => [item.slug, item.title]))
)

let requestToken = 0

const chosenFilters = ref({});

const applyFilterResponse = (response) => {
  filter.value = response?.filter?.filter(item => item.type === 'range' || (Array.isArray(item.values) ? item.values.length : Object.values(item.values).length > 0))

  ;(response.filter || []).forEach(item => {
    if (item.type === 'range' && item.values?.length) {
      const minEntry = item.values.find(v => v.slug === 'min')
      const maxEntry = item.values.find(v => v.slug === 'max')
      if (minEntry !== undefined && maxEntry !== undefined) {
        const min = parseFloat(minEntry.value) || 0
        const max = parseFloat(maxEntry.value) || 0
        const existing = rangeAbsoluteValues.value[item.slug]
        if (!existing || min < existing.min || max > existing.max) {
          rangeAbsoluteValues.value[item.slug] = { min, max }
        }
      } else {
        const vals = item.values.map(v => parseFloat(v.value) || 0).filter(v => v > 0)
        if (vals.length) {
          const min = Math.min(...vals)
          const max = Math.max(...vals)
          const existing = rangeAbsoluteValues.value[item.slug]
          if (!existing || min < existing.min || max > existing.max) {
            rangeAbsoluteValues.value[item.slug] = { min, max }
          }
        }
      }
    }
  })

  chosenFilters.value = Array.isArray(response?.chosen_filters)
    ? {}
    : response?.chosen_filters || {}

  for (const filterKey in chosenFilters.value) {
    if (Array.isArray(chosenFilters.value[filterKey])) {
      chosenFilters.value[filterKey] = [...new Set(chosenFilters.value[filterKey])];
    }
  }
  key.value = Math.random()
}

const getFilters = (fullUrl, retries = 1) => {
  const token = ++requestToken
  return useCustomFetch(fullUrl, {
    query: {
      search: route.query.search,
    }
  }, true).then((response) => {
    if (token !== requestToken) return null

    applyFilterResponse(response)
    return chosenFilters.value
  }).catch((error) => {
    if (token !== requestToken) return null
    if (retries > 0) {
      console.error('Repeating...', error);
      return getFilters(fullUrl, retries - 1);
    } else {
      console.error('Error after repeating', error);
      throw error;
    }
  });
}

const getTabSlotName = (index) => {
  return `tab-content-${index}`;
}

const handleClick = (e) => {
  const {name, value} = e

  if (typeof chosenFilters.value[name] === 'string') {
    delete chosenFilters.value[name]
    updateBrowserUrl(chosenFilters.value)
    return
  }

  if (!chosenFilters.value[name]) {
    chosenFilters.value[name] = []
  }

  if (chosenFilters.value[name].includes(value)) {
    chosenFilters.value[name] = chosenFilters.value[name].filter(item => item !== value)
  } else {
    chosenFilters.value[name].push(value)
  }

  if (name === 'size' && chosenFilters.value[name].includes(value)) {
    const currentFilter = document.querySelector(`.filter-grid[data-filter="size"] [data-slug="${value}"]`)

    if (currentFilter) {
      const subGroup = currentFilter.getAttribute('data-sub-group')
      const group = currentFilter.getAttribute('data-group')

      if (subGroup) {
        chosenFilters.value['size'] = chosenFilters.value['size'].filter(item => item !== subGroup)
      }

      if (group) {
        chosenFilters.value['size'] = chosenFilters.value['size'].filter(item => item !== group)
      }

      if (!subGroup && !group) {
        const groupValues = document.querySelectorAll(`.filter-grid[data-filter="size"] .active[data-group="${value}"]`)
        const values = Array.from(groupValues).map(item => item.getAttribute('data-slug'))

        chosenFilters.value['size'] = chosenFilters.value['size'].filter(item => !values.includes(item))
      } else if (group) {
        const groupValues = document.querySelectorAll(`.filter-grid[data-filter="size"] .active[data-group=${group}][data-sub-group="${value}"]`)
        const values = Array.from(groupValues).map(item => item.getAttribute('data-slug'))

        chosenFilters.value['size'] = chosenFilters.value['size'].filter(item => !values.includes(item))
      }
    }
  }

  if (chosenFilters.value[name].length === 0) {
    delete chosenFilters.value[name]
  }

  updateBrowserUrl(chosenFilters.value);
}

const handleRangeChange = ({ name, value }) => {
  if (value) {
    chosenFilters.value[name] = value
  } else {
    delete chosenFilters.value[name]
  }
  updateBrowserUrl(chosenFilters.value)
}

const clearFilter = () => {
  chosenFilters.value = {}
  updateBrowserUrl(chosenFilters.value);
}

function createUrlFromFilters(filters) {
  let urlSegments = [];

  for (const [key, values] of Object.entries(filters)) {
    if (Array.isArray(values) && Object.values(values).length > 0) {
      urlSegments.push(`${key}=${values.join('_')}`);
    } else if (values && Object.values(values).length > 0) {
      urlSegments.push(`${key}=${values}`);
    }
  }

  return urlSegments.length ? '/' + urlSegments.join('/') : '';
}

function updateBrowserUrl(filters) {
  const filterPath = createUrlFromFilters(filters);
  if (!process.client) {
    return
  }

  const path = normalizePath('/' + slug.value + filterPath)
  const routerPath = path === '/' ? '/' : `${path}/`
  const query = {...route.query}
  delete query.page

  // Plain history.replaceState leaves vue-router.current at /klinker.
  // On the next product navigation, vue-router push() does replaceState(current)
  // first and wipes the filter URL from history — Back then returns to /klinker.
  router.replace({path: routerPath, query})

  window.dispatchEvent(new CustomEvent('urlReplaced', {
    detail: {
      hasCategoryFilters: Object.keys(filters).length > 0,
    },
  }))

  getFilters('/products-grid/filter' + path)

  emit('change', {
    slug: path,
    chosenFilters: chosenFilters.value,
  })
}

const syncFromPath = async (path) => {
  const normalizedPath = normalizePath(path)
  slug.value = categorySlugFromPath(normalizedPath)

  await getFilters(`/products-grid/filter${normalizedPath === '/' ? '' : normalizedPath}`)

  return {
    slug: normalizedPath,
    chosenFilters: chosenFilters.value,
  }
}

watch(() => props.openFilter, () => {
  active.value = true
})

onMounted(() => {
  const routePath = normalizePath(
    '/' + (Array.isArray(route.params.slug) ? route.params.slug.join('/') : route.params.slug)
  )
  getFilters(`/products-grid/filter${routePath === '/' ? '' : routePath}`)
})

defineExpose({
  handleClick, handleRangeChange, clearFilter, filterTitles, syncFromPath
})
</script>

<template>
  <div class="filter">
    <SideSlider :title="$t('base.filter_action')" @close="active=false" :active="active">
      <SpoilerTabs v-if="filter.length" :classic-separator="false" :arrows="false"
                   :tabs="filter.map(filterItem => ({title:filterItem.title, opened:filterItem.opened}))">
        <template v-slot:[getTabSlotName(index)] v-for="(filterItem,index) in filter">
          <div class="filter-block" :class="[filterItem.slug, {'text':filterItem.is_text}]">
            <FilterGrid :chosenFilters="chosenFilters" :key="key" :absoluteRanges="rangeAbsoluteValues" @click="handleClick" @range-change="handleRangeChange" :filter-item="filterItem"/>
          </div>
        </template>
      </SpoilerTabs>
    </SideSlider>
  </div>
</template>

<style scoped lang="scss">
button {
  width: 139px !important;
  height: 48px !important;
  font-size: 16px;
  position: fixed;
  left: -46px;
  top: 358px;
  transform: rotate(-90deg);
  z-index: 99;
  font-weight: 500;
  line-height: normal;
  text-transform: uppercase;
  letter-spacing: .96px;
  gap: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.filter {
  &:deep(.tab) {
    height: fit-content !important;

    .heading, .tab-content {
      padding: 0 24px;
    }

    .heading {
      text-transform: uppercase;
    }
  }

  .filter-block {
    max-height: 420px;
    overflow-y: auto;
    gap: 18px;
    margin-left: -10px;
    padding-bottom: 20px;

    &.text {
      margin-left: 0;
    }
  }

  &:deep(.main-block.active) {
    @include mobile {
      max-width: 86%;
    }
  }
}
</style>
